import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { taskAPI, projectAPI } from '../../../api/services';

const STATUS_ORDER = ['in_progress', 'todo', 'blocked', 'review', 'done'];
const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do', in_progress: 'In Progress', blocked: 'Blocked', review: 'Review', done: 'Done',
};
const STATUS_DOT: Record<string, string> = {
  todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500', review: 'bg-yellow-500', done: 'bg-green-500',
};
const PRIORITY_PILL: Record<string, string> = {
  critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white',
  medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600',
};

// ─── Quick-edit popover for inline task editing ─────────────────
const TaskQuickEdit: React.FC<{
  task: any;
  projectId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ task, projectId, onClose, onSaved }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || '',
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
    storyPoints: task.storyPoints || '',
    status: task.status || 'todo',
  });
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Fetch project members for assignment
  useEffect(() => {
    projectAPI.getProjectById(projectId).then(res => {
      const p = res.data?.data || res.data;
      setMembers(p?.members || []);
    }).catch(() => {});
  }, [projectId]);

  const save = async (field: string, value: any) => {
    setSaving(true);
    try {
      await taskAPI.updateTask(task._id, { [field]: value });
      onSaved();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleAssign = async (userId: string, name: string) => {
    setAssigning(true);
    try {
      await taskAPI.assignTask(task._id, userId);
      onSaved();
    } catch { /* ignore */ }
    setAssigning(false);
  };

  const currentAssignees = task.assignedTo ?? [];
  const labelCls = 'text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1';

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72"
      onClick={e => e.stopPropagation()}
    >
      {/* Title */}
      <div className="mb-3">
        <div className={labelCls}>Title</div>
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          onBlur={() => form.title.trim() && form.title !== task.title && save('title', form.title.trim())}
          onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400"
        />
      </div>

      {/* Description */}
      <div className="mb-3">
        <div className={labelCls}>Description</div>
        <textarea
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          onBlur={() => form.description !== (task.description || '') && save('description', form.description)}
          placeholder="Add description..."
          rows={2}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 resize-none"
        />
      </div>

      {/* Priority + Status row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <div className={labelCls}>Priority</div>
          <select
            value={form.priority}
            onChange={e => { setForm(f => ({ ...f, priority: e.target.value })); save('priority', e.target.value); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
          >
            <option value="">None</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div>
          <div className={labelCls}>Status</div>
          <select
            value={form.status}
            onChange={e => {
              const newStatus = e.target.value;
              setForm(f => ({ ...f, status: newStatus }));
              taskAPI.updateStatus(task._id, newStatus).then(onSaved).catch((err: any) => {
                setForm(f => ({ ...f, status: task.status }));
                const msg = err?.response?.data?.message || 'Failed to update status';
                alert(msg);
              });
            }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
          >
            {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
      </div>

      {/* Due date + SP row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <div className={labelCls}>Due Date</div>
          <input
            type="date"
            value={form.dueDate}
            onChange={e => { setForm(f => ({ ...f, dueDate: e.target.value })); save('dueDate', e.target.value || undefined); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
          />
        </div>
        <div>
          <div className={labelCls}>Story Points</div>
          <input
            type="number"
            min={0}
            value={form.storyPoints}
            onChange={e => setForm(f => ({ ...f, storyPoints: e.target.value }))}
            onBlur={() => { const v = form.storyPoints ? Number(form.storyPoints) : 0; if (v !== (task.storyPoints || 0)) save('storyPoints', v); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
            placeholder="0"
          />
        </div>
      </div>

      {/* Assignees */}
      <div className="mb-2">
        <div className={labelCls}>Assignees</div>
        {currentAssignees.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {currentAssignees.map((a: any, i: number) => (
              <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{a.name || 'User'}</span>
            ))}
          </div>
        )}
        {members.length > 0 && (
          <select
            defaultValue=""
            onChange={e => {
              const m = members.find((m: any) => m.userId === e.target.value);
              if (m) handleAssign(m.userId, m.name);
              e.target.value = '';
            }}
            disabled={assigning}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400 text-gray-500"
          >
            <option value="" disabled>+ Assign member...</option>
            {members.filter((m: any) => !currentAssignees.some((a: any) => a.userId === m.userId)).map((m: any) => (
              <option key={m.userId} value={m.userId}>{m.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Delete */}
      <div className="mt-2 pt-2 border-t border-gray-100">
        {confirmDelete ? (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-red-600">Delete this task?</span>
            <div className="flex gap-1.5">
              <button
                onClick={async () => {
                  try { await taskAPI.deleteTask(task._id); onSaved(); } catch { /* ignore */ }
                }}
                className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button onClick={() => setConfirmDelete(false)} className="text-[10px] text-gray-500 px-2 py-0.5 rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 text-[10px] text-gray-400 hover:text-red-600 transition-colors w-full">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
            Delete task
          </button>
        )}
      </div>

      {saving && <div className="text-[10px] text-blue-500 text-center mt-1">Saving...</div>}
    </div>
  );
};

interface Props {
  projectId: string;
  tasks: any[];
  features: any[];
  onTaskClick: (taskId: string) => void;
  onRefresh: () => void;
}

const StatusGroupedList: React.FC<Props> = ({ projectId, tasks, features, onTaskClick, onRefresh }) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(['done']));
  const [collapsedFeatures, setCollapsedFeatures] = useState<Set<string>>(new Set());
  const [addingStatus, setAddingStatus] = useState<string | null>(null);
  const [addingFeature, setAddingFeature] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusError, setStatusError] = useState<{ taskId: string; message: string } | null>(null);

  const handleStatusUpdate = useCallback((taskId: string, newStatus: string) => {
    setStatusError(null);
    taskAPI.updateStatus(taskId, newStatus).then(onRefresh).catch((err: any) => {
      const msg = err?.response?.data?.message || 'Failed to update status';
      setStatusError({ taskId, message: msg });
      setTimeout(() => setStatusError(s => s?.taskId === taskId ? null : s), 5000);
    });
  }, [onRefresh]);

  // Optimistic tasks — kept until real data includes a task with matching title+status
  const [optimisticTasks, setOptimisticTasks] = useState<any[]>([]);

  // Auto-clear optimistic tasks when real data arrives with matching tasks
  const allTasks = useMemo(() => {
    if (optimisticTasks.length === 0) return tasks;
    // Filter out optimistic tasks whose real counterpart now exists in `tasks`
    const remaining = optimisticTasks.filter(ot =>
      !tasks.some(t => t.title === ot.title && t.status === ot.status && t.parentId?.toString() === ot.parentId?.toString())
    );
    if (remaining.length !== optimisticTasks.length) {
      // Schedule cleanup outside render
      setTimeout(() => setOptimisticTasks(remaining), 0);
    }
    return [...tasks, ...remaining];
  }, [tasks, optimisticTasks]);

  const toggleGroup = (s: string) => setCollapsedGroups(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const toggleFeature = (k: string) => setCollapsedFeatures(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const handleAddTask = async (status: string, featureId?: string) => {
    if (!newTaskTitle.trim() || submitting) return;
    const title = newTaskTitle.trim();
    setSubmitting(true);

    // Optimistic: add task locally immediately
    const tempId = `temp_${Date.now()}`;
    const tempTask: any = {
      _id: tempId,
      title,
      status,
      parentType: featureId ? 'feature' : 'project',
      parentId: featureId || projectId,
      assignedTo: [],
      storyPoints: 0,
      createdBy: {},
      _optimistic: true,
    };
    setOptimisticTasks(prev => [...prev, tempTask]);
    setNewTaskTitle('');
    setSubmitting(false);

    // Fire API in background, then silently refresh — optimistic task
    // stays visible until real data arrives via the allTasks memo
    taskAPI.createTask({
      title,
      description: title,
      status,
      parentType: featureId ? 'feature' : 'project',
      parentId: featureId || projectId,
    } as any).catch((err: any) => {
      // Remove on failure and show error
      setOptimisticTasks(prev => prev.filter(t => t._id !== tempId));
      const msg = err.response?.data?.message || 'Failed to create task';
      alert(msg);
    }).finally(() => onRefresh());
  };

  const groupedTasks = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const s of STATUS_ORDER) g[s] = [];
    for (const t of allTasks) { const s = t.status || 'todo'; if (g[s]) g[s].push(t); else g[s] = [t]; }
    return g;
  }, [allTasks]);

  const featureMap = useMemo(() => {
    const m: Record<string, any> = {};
    for (const f of features) m[f._id] = f;
    return m;
  }, [features]);

  const fmtDate = (d?: string) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/');
  };

  const pct = (done: number, total: number) => total > 0 ? Math.round((done / total) * 100) : 0;

  // Column header row
  const ColHeader = () => (
    <div className="grid grid-cols-12 gap-1 px-5 py-2 text-xs text-gray-400 font-medium select-none">
      <div className="col-span-4 flex items-center gap-1">
        <span className="text-gray-300">Aa</span> Name
      </div>
      <div className="col-span-2 flex items-center gap-1">
        <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
        Assignee
      </div>
      <div className="col-span-1">Priority</div>
      <div className="col-span-1">Progress</div>
      <div className="col-span-1 flex items-center gap-1">
        <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25" /></svg>
        Due
      </div>
      <div className="col-span-1">SP</div>
      <div className="col-span-1">Status</div>
      <div className="col-span-1">Owner</div>
    </div>
  );

  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  const TaskRow = ({ task, indent = false }: { task: any; indent?: boolean }) => {
    const assignees = task.assignedTo ?? [];
    return (
      <div className="relative">
        <div
          className="grid grid-cols-12 gap-1 py-2.5 text-sm hover:bg-gray-50/80 cursor-pointer group/task transition-colors"
          style={{ paddingLeft: indent ? '48px' : '20px', paddingRight: '20px' }}
          onClick={() => !task._optimistic && onTaskClick(task._id)}
        >
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <button
              className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all ${task.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`}
              onClick={e => { e.stopPropagation(); if (!task._optimistic) handleStatusUpdate(task._id, task.status === 'done' ? 'todo' : 'done'); }}
            />
            <span className={`truncate ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`} title={`${task.title}${task.description ? '\n' + task.description : ''}`}>
              {task.title}
            </span>
          </div>
          <div className="col-span-2 flex items-center">
            {assignees.length > 0 ? (
              <div className="flex -space-x-1">
                {assignees.slice(0, 2).map((a: any, i: number) => (
                  <div key={i} className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center border-2 border-white" title={a.name}>{(a.name || '?')[0]}</div>
                ))}
                {assignees.length > 2 && <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-xs flex items-center justify-center border-2 border-white">+{assignees.length - 2}</div>}
              </div>
            ) : <span className="text-xs text-gray-300">—</span>}
          </div>
          <div className="col-span-1 flex items-center">
            {task.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[task.priority] || 'bg-gray-200 text-gray-600'}`}>{task.priority}</span>}
          </div>
          <div className="col-span-1 flex items-center">
            {task.subtasks?.length > 0 && (() => {
              const done = task.subtasks.filter((s: any) => s.completed).length;
              const p = pct(done, task.subtasks.length);
              return (
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${p === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${p}%` }} />
                  </div>
                  <span className="text-xs text-gray-400">{p}%</span>
                </div>
              );
            })()}
          </div>
          <div className="col-span-1 flex items-center text-xs text-gray-500">{fmtDate(task.dueDate)}</div>
          <div className="col-span-1 flex items-center text-xs text-blue-600 font-medium">{task.storyPoints > 0 ? task.storyPoints : ''}</div>
          <div className="col-span-1 flex items-center justify-between">
            <div className={`w-2 h-2 rounded-full ${STATUS_DOT[task.status] || 'bg-gray-300'}`} title={STATUS_LABELS[task.status]} />
            {/* Quick edit button — appears on hover */}
            {!task._optimistic && (
              <button
                onClick={e => { e.stopPropagation(); setEditingTaskId(editingTaskId === task._id ? null : task._id); }}
                className="opacity-0 group-hover/task:opacity-100 transition-opacity text-gray-400 hover:text-blue-600"
                title="Quick edit"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
              </button>
            )}
          </div>
          <div className="col-span-1 flex items-center text-xs text-gray-400 truncate">
            {task.createdBy?.name?.split(' ')[0] || ''}
          </div>
        </div>
        {/* Approval gate error banner */}
        {statusError?.taskId === task._id && (
          <div className="flex items-center gap-2 mx-5 mb-1 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs animate-in fade-in slide-in-from-top-1">
            <svg className="w-4 h-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span className="flex-1">{statusError.message}</span>
            <button onClick={() => setStatusError(null)} className="text-amber-400 hover:text-amber-600">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}
        {/* Quick edit popover */}
        {editingTaskId === task._id && (
          <TaskQuickEdit
            task={task}
            projectId={projectId}
            onClose={() => setEditingTaskId(null)}
            onSaved={() => { setEditingTaskId(null); onRefresh(); }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      {STATUS_ORDER.map(status => {
        const tasksInGroup = groupedTasks[status] || [];
        if (tasksInGroup.length === 0 && status !== 'todo') return null;
        const isExpanded = !collapsedGroups.has(status);

        // Group by feature
        const featureGroups: Record<string, any[]> = {};
        const standalone: any[] = [];
        for (const t of tasksInGroup) {
          if (t.parentType === 'feature' && t.parentId && featureMap[t.parentId]) {
            if (!featureGroups[t.parentId]) featureGroups[t.parentId] = [];
            featureGroups[t.parentId].push(t);
          } else standalone.push(t);
        }

        const totalSP = tasksInGroup.reduce((s: number, t: any) => s + (t.storyPoints || 0), 0);

        return (
          <div key={status} className="mb-1">
            {/* Group header — clean, minimal */}
            <div
              className="flex items-center gap-2 py-2 px-5 cursor-pointer select-none hover:bg-gray-50/50"
              onClick={() => toggleGroup(status)}
            >
              <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <div className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
              <span className="text-xs font-bold text-gray-700">{STATUS_LABELS[status]}</span>
              <span className="text-xs text-gray-400">{tasksInGroup.length}</span>
            </div>

            {isExpanded && (
              <>
                <ColHeader />

                {/* Feature groups */}
                {Object.entries(featureGroups).map(([fId, fTasks]) => {
                  const f = featureMap[fId];
                  const fExpanded = !collapsedFeatures.has(`${status}_${fId}`);
                  const fDone = fTasks.filter((t: any) => t.status === 'done').length;
                  const fPct = pct(fDone, fTasks.length);
                  return (
                    <React.Fragment key={fId}>
                      <div
                        className="grid grid-cols-12 gap-1 px-5 py-2 cursor-pointer hover:bg-blue-50/50 transition-colors"
                        onClick={() => toggleFeature(`${status}_${fId}`)}
                      >
                        <div className="col-span-4 flex items-center gap-2">
                          <svg className={`w-3 h-3 text-blue-400 transition-transform ${fExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                          </svg>
                          <span className="text-sm font-medium text-blue-700 truncate">{f?.name || 'Feature'}</span>
                          <span className="text-xs text-gray-400">{fTasks.length}</span>
                        </div>
                        <div className="col-span-2" />
                        <div className="col-span-1">
                          {f?.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[f.priority] || 'bg-gray-200'}`}>{f.priority}</span>}
                        </div>
                        <div className="col-span-1 flex items-center">
                          <div className="flex items-center gap-1.5 w-full">
                            <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${fPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${fPct}%` }} />
                            </div>
                            <span className="text-xs text-gray-400">{fPct}%</span>
                          </div>
                        </div>
                        <div className="col-span-1 text-xs text-gray-500">{fmtDate(f?.dates?.estimatedEndDate)}</div>
                        <div className="col-span-1 text-xs text-blue-600 font-medium">{f?.storyPoints > 0 ? f.storyPoints : ''}</div>
                        <div className="col-span-1">
                          {f?.approvalStatus && f.approvalStatus !== 'not_required' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${f.approvalStatus === 'approved' ? 'bg-green-100 text-green-700' : f.approvalStatus === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>{f.approvalStatus === 'approved' ? '✓' : '⏳'}</span>
                          )}
                        </div>
                        <div className="col-span-1 text-xs text-gray-400 truncate">{f?.owner?.name?.split(' ')[0] || ''}</div>
                      </div>
                      {fExpanded && (
                        <>
                          {fTasks.map((t: any) => <TaskRow key={t._id} task={t} indent />)}
                          {/* Quick add task under feature */}
                          <div className="pl-12 pr-5 py-1">
                            {addingFeature === `${status}_${fId}` ? (
                              <div className="flex items-center gap-2">
                                <input
                                  autoFocus
                                  value={newTaskTitle}
                                  onChange={e => setNewTaskTitle(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleAddTask(status, fId); if (e.key === 'Escape') { setAddingFeature(null); setNewTaskTitle(''); } }}
                                  placeholder="Task name"
                                  className="flex-1 text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none focus:ring-0 bg-transparent"
                                />
                                <button onClick={() => handleAddTask(status, fId)} disabled={submitting} className="text-xs text-blue-600 hover:text-blue-700">Add</button>
                                <button onClick={() => { setAddingFeature(null); setNewTaskTitle(''); }} className="text-xs text-gray-400">Cancel</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setAddingFeature(`${status}_${fId}`); setAddingStatus(null); setNewTaskTitle(''); }}
                                className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 py-0.5"
                              >
                                <span className="text-sm leading-none">+</span> Add task
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Standalone tasks */}
                {standalone.map(t => <TaskRow key={t._id} task={t} />)}

                {/* Summary row */}
                {tasksInGroup.length > 0 && (
                  <div className="grid grid-cols-12 gap-1 px-5 py-1.5 text-xs text-gray-400">
                    <div className="col-span-4" />
                    <div className="col-span-2" />
                    <div className="col-span-1" />
                    <div className="col-span-1" />
                    <div className="col-span-1" />
                    <div className="col-span-1 font-medium">SUM {totalSP}</div>
                    <div className="col-span-2" />
                  </div>
                )}

                {/* Add task inline */}
                <div className="px-5 py-1">
                  {addingStatus === status ? (
                    <div className="flex items-center gap-2 pl-6">
                      <input
                        autoFocus
                        value={newTaskTitle}
                        onChange={e => setNewTaskTitle(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddTask(status); if (e.key === 'Escape') { setAddingStatus(null); setNewTaskTitle(''); } }}
                        placeholder="Task name"
                        className="flex-1 text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none focus:ring-0 bg-transparent"
                      />
                      <button onClick={() => handleAddTask(status)} disabled={submitting} className="text-xs text-blue-600 hover:text-blue-700">Add</button>
                      <button onClick={() => { setAddingStatus(null); setNewTaskTitle(''); }} className="text-xs text-gray-400">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => { setAddingStatus(status); setAddingFeature(null); setNewTaskTitle(''); }} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 pl-6 py-1">
                      <span className="text-lg leading-none">+</span> Add Task
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StatusGroupedList;
